import { BROWSER_CAPABILITIES } from 'utils/browserCapabilities';

type TMediaElementMethod = 'load' | 'pause' | 'play';

/**
 * Wait this time if action does not return promise, ms
 *
 * @type {number}
 */
const TIMEOUT = 150;

/**
 * Action promises mapped to player objects
 *
 * @type {WeakMap}
 */
const promises = new WeakMap();

/**
 * Map of values to help us define whether we can play media.
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/networkState
 */
export const NETWORK_STATES = {
  /**
   * There is no data yet. Also, "readyState" property is HAVE_NOTHING.
   * About ready state: https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
   */
  EMPTY: 0,

  /**
   * HTMLMediaElement is active and has selected a resource, but is not using the network.
   */
  IDLE: 1,

  /**
   * The browser is downloading HTMLMediaElement data.
   */
  LOADING: 2,

  /**
   * No HTMLMediaElement src found.
   */
  NO_SOURCE: 3,
};

/**
 * Map of values to help us define whether we can play media.
 *
 * @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
 */
export const READY_STATES = {
  /**
   * No information is available about the media resource.
   */
  HAVE_NOTHING: 0,

  /**
   * Enough of the media resource has been retrieved that the metadata attributes are initialized.
   * Seeking will no longer raise an exception.
   */
  HAVE_METADATA: 1,

  /**
   * Data is available for the current playback position, but not enough to actually play more than one frame.
   */
  HAVE_CURRENT_DATA: 2,

  /**
   * Data for the current playback position as well as for at least a little bit of time into
   * the future is available (in other words, at least two frames of video, for example).
   */
  HAVE_FUTURE_DATA: 3,

  /**
   * Enough data is available—and the download rate is high enough—that the media can be played
   * through to the end without interruption.
   */
  HAVE_ENOUGH_DATA: 4,
};

type TPlayerAction = TMediaElementMethod | ((player: HTMLMediaElement) => Promise<void>);

/**
 * Some browsers (e.g. Firefox for Android) show blocking error "Sorry, this video can not be played"
 * if "src" doesn't provide data which exists. E.g. we're looking for audio stream file, but model
 * is broadcasting being muted and there is no audio recorded on the server.
 *
 * @param {Object} player
 * @param {string} action
 */
export const checkPlayAvailability = (player: HTMLMediaElement, action: TPlayerAction) => {
  const isBadState = player.readyState === READY_STATES.HAVE_NOTHING;
  const isBadNetwork =
    player.networkState !== NETWORK_STATES.LOADING && player.networkState !== NETWORK_STATES.IDLE;

  if (BROWSER_CAPABILITIES.firefox && action === 'play' && isBadState && isBadNetwork) {
    throw new Error('Can not play audio');
  }
};

export const performAction = (
  player: HTMLMediaElement,
  action: TPlayerAction,
): null | Promise<void> | void => {
  if (!(player instanceof HTMLMediaElement)) {
    // Player seems to be unmounted
    return null;
  }

  checkPlayAvailability(player, action);

  if (typeof action === 'function') {
    // Function is provided
    return action(player);
  }

  // Call player method
  return player[action]();
};

/**
 * Add media action to the queue for a player provided
 *
 * @param {HTMLMediaElement} player HTML media element instance
 * @param {string|function} action Player method name or a callback
 * @returns {Promise}
 */
export const addMediaAction = (player: HTMLMediaElement, action: TPlayerAction): Promise<void> => {
  const lastPromise = promises.get(player);
  const activePromise = lastPromise || Promise.resolve();

  const promise = activePromise
    .then(() => {
      const result = performAction(player, action);

      if (result?.then) {
        // Wrap any then-able with promise
        return Promise.resolve(result);
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(result);
        }, TIMEOUT);
      });
    })
    .then(() => {
      promises.delete(player);
    })
    .catch((error: Error) => {
      promises.delete(player);

      throw error;
    });

  promises.set(player, promise);

  return promise;
};
